import { isSymbol } from "@shared/types/utils.ts";
import { useContext, useEffect, useMemo } from "react";
import {
  BotData,
  BotType,
  LanguageNameSpaces,
  MarketplaceSubGroup,
  OpenMessage,
  SortingOrder,
  SubGroup,
} from "@shared/types";
import { getBotsInvalidGroupMessage } from "@shared/features/bots/utils.ts";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import {
  BotFormType,
  BotSubGroup,
  Error,
  FormDataType,
} from "@shared/features/bots/BotForm/common/types.ts";
import { marketplaceSelectors } from "@shared/slices/marketplace.slice.ts";
import { useSelector } from "react-redux";
import { getSortedDataBySortSelect } from "@shared/utils/general.ts";
import { GroupsSort } from "@shared/features/newMarketplace/types.ts";
import { filterByExchanges } from "@shared/features/filters/utils.tsx";
import {
  MARKETPLACE_SORT_TO_PATH,
  MARKETPLACE_STRING_SORTS,
} from "@shared/features/newMarketplace/constants.ts";
import {
  useClientsData,
  useIsSubUser,
  useUser,
} from "@shared/utils/hooks/misc.ts";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography.ts";
import CreatePersonalGroupButton from "features/bots/Groups/CreatePersonalGroupButton.tsx";
import { useEmptyState } from "common/Icons/useEmptyState.tsx";
import { EmptyStatesType } from "common/Icons/types.ts";
import styled, { useTheme } from "styled-components";
import useSignalsSubGroupSelector from "features/tradingTerminal/General/useSignalsSubGroupSelector.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { GRACE_TARGET_EXCHANGE_FEATURE_FLAG } from "@shared/features/bots/BotForm/common/constants.ts";

interface useSubGroupSelectorProps {
  querySubGroupId?: number;
  onInvalidGroup?: () => void;
  openMessage: OpenMessage;
  setOpenPersonalGroupModal?: (open: boolean) => void;
}

const useSubGroupSelector = ({
  querySubGroupId,
  onInvalidGroup,
  openMessage,
  setOpenPersonalGroupModal,
}: useSubGroupSelectorProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { user } = useUser();
  const {
    botType,
    botGeneralData,
    isEdit,
    botFormType,
    getGroupsDataStatus,
    subGroup,
    setSubGroup,
    setSubGroupBotConfiguration,
    initialized,
    addError,
    removeError,
    isBacktesting,
    addInitializedData,
    isChannelConfiguration,
  } = useContext(BotFormContext)!;
  const groupsData = getGroupsDataStatus.data;
  const groupsSelector = useMemo(
    () => marketplaceSelectors.selectSubgroups(false),
    []
  );
  const marketplaceGroups = useSelector(groupsSelector);
  const { activeClientsData } = useClientsData();
  const isAssetManager = user?.isAssetManager();
  const emptyStates = useEmptyState();
  const { isNewLook } = useTheme();
  const isSubUser = useIsSubUser();

  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isCopyPublic = botFormType === BotFormType.COPY_PUBLIC;
  const isBotEdit =
    botFormType === BotFormType.EDIT || botFormType === BotFormType.BULK_EDIT;

  const allHistoryUserGroupIds = useMemo(
    () => new Set(groupsData?.map((group) => group.id)),
    [groupsData]
  );
  const activeGroups = useMemo(() => {
    return groupsData?.filter(
      (group) =>
        group.allow_auto_trading && group.allow_follow && group.is_active
    );
  }, [groupsData]);
  const activeUserGroupIds = useMemo(
    () => new Set(activeGroups?.map((group) => group.id)),
    [activeGroups]
  );

  const userGroupIdToData = useMemo(() => {
    return activeGroups?.reduce((acc, group) => {
      acc[group.id] = group;
      return acc;
    }, {} as Record<number, SubGroup>);
  }, [activeGroups]);

  const marketplaceGroupIdToData = useMemo(() => {
    return marketplaceGroups?.reduce((acc, group) => {
      acc[group.group_id] = group;
      return acc;
    }, {} as Record<number, MarketplaceSubGroup>);
  }, [marketplaceGroups]);

  const userGroups = useMemo(
    () => activeGroups?.filter((group) => !group.is_personal) || [],
    [activeGroups]
  );

  const personalGroups = useMemo(
    () => activeGroups?.filter((group) => group.is_personal) || [],
    [activeGroups]
  );

  const getGroupExtraData = (groupId?: number) => {
    if (!groupId || !userGroupIdToData) return;
    const marketplaceGroup = marketplaceGroupIdToData[groupId];
    const userGroup = userGroupIdToData[groupId];
    const hasFreeTrial = marketplaceGroup?.has_free_trial;
    const usedFreeTrial = hasFreeTrial && allHistoryUserGroupIds.has(groupId);
    return {
      isMarketplaceGroup: !!marketplaceGroup,
      hasFreeTrial,
      usedFreeTrial,
      isUserGroupMember: !!userGroup,
      freeTrialAvailable: hasFreeTrial && !usedFreeTrial,
    };
  };

  const getGroupError = () => {
    if (
      (!isSignalsBot && !isTradingViewSignalsBot) ||
      !activeGroups ||
      !initialized
    )
      return;
    if (!subGroup) return t("messages.errors.choose_group");
    if (!selectedGroupExtraData?.isUserGroupMember) {
      if (selectedGroupExtraData?.usedFreeTrial)
        return t("messages.errors.already_used_free_trial");
      else if (!selectedGroupExtraData?.freeTrialAvailable)
        return isCopyPublic
          ? t("messages.errors.not_group_member")
          : t("messages.errors.no_longer_group_member");
    }
  };

  const selectedGroupExtraData = useMemo(
    () => getGroupExtraData(subGroup?.id),
    [subGroup, userGroupIdToData, marketplaceGroupIdToData]
  );
  const subGroupError = getGroupError();

  useEffect(() => {
    if (subGroupError) addError(Error.SUB_GROUP);
    else removeError(Error.SUB_GROUP);
  }, [subGroupError]);

  const addSubGroupExtraData = (subGroup: BotSubGroup) => {
    const groupExtraData = getGroupExtraData(subGroup.id);
    const groupData: BotSubGroup = { ...subGroup };
    if (
      !groupExtraData?.isUserGroupMember &&
      groupExtraData?.freeTrialAvailable
    ) {
      groupData.isFreeTrialGroup = true;
      groupData.groupData = marketplaceGroupIdToData?.[subGroup.id];
    }
    return groupData;
  };

  useEffect(() => {
    if (subGroup) {
      const groupWithExtraData = addSubGroupExtraData(subGroup);
      if (subGroup.isFreeTrialGroup !== groupWithExtraData.isFreeTrialGroup) {
        setSubGroup(groupWithExtraData);
      }
    }
  }, [
    subGroup,
    userGroupIdToData,
    marketplaceGroupIdToData,
    allHistoryUserGroupIds,
  ]);

  const sortedGroups = useMemo(() => {
    return getSortedDataBySortSelect<MarketplaceSubGroup, GroupsSort>(
      marketplaceGroups,
      GroupsSort.SCORE,
      SortingOrder.DESCEND,
      MARKETPLACE_SORT_TO_PATH,
      MARKETPLACE_STRING_SORTS
    ).slice(0, 10);
  }, [marketplaceGroups]);

  const accountsExchanges = useMemo(
    () => activeClientsData?.map((client) => client.exchange) || [],
    [activeClientsData]
  );

  const getFreeTrialGroups = () => {
    return filterByExchanges(sortedGroups, accountsExchanges)
      .filter((group) => getGroupExtraData(group.group_id)?.freeTrialAvailable)
      .slice(0, 5)
      .filter((group) => !activeUserGroupIds.has(group.group_id))
      .map((group) => ({
        id: group.group_id,
        name: group.name,
        image: group.image_path,
        isFreeTrialGroup: true,
        groupData: group,
      }));
  };

  const getSelectorGroups = () => {
    const freeTrialGroups = getFreeTrialGroups();
    const userNotInGroup =
      subGroup && !selectedGroupExtraData?.isUserGroupMember;
    const shouldAddUserGroup =
      userNotInGroup && !selectedGroupExtraData?.freeTrialAvailable;
    const shouldAddFreeTrialGroup =
      userNotInGroup &&
      selectedGroupExtraData?.freeTrialAvailable &&
      !freeTrialGroups.find((group) => group.id === subGroup.id);
    return {
      personalGroups: isAssetManager ? personalGroups : [],
      userGroups: shouldAddUserGroup ? [...userGroups, subGroup] : userGroups,
      freeTrialGroups: shouldAddFreeTrialGroup
        ? [...freeTrialGroups, subGroup]
        : freeTrialGroups,
    };
  };

  const selectorGroups = useMemo(() => {
    return getSelectorGroups();
  }, [sortedGroups, selectedGroupExtraData, accountsExchanges]);

  const subGroupIdToData = useMemo(() => {
    return [
      ...selectorGroups.personalGroups,
      ...selectorGroups.userGroups,
      ...selectorGroups.freeTrialGroups,
    ]?.reduce((acc: Record<number, SubGroup>, subGroupData) => {
      acc[subGroupData.id] = subGroupData;
      return acc;
    }, {});
  }, [selectorGroups]);

  const handleSubGroupChange = (subGroupId: number) => {
    setSubGroup(subGroupIdToData?.[subGroupId]);
    setSubGroupBotConfiguration(undefined);
  };

  const { onSubGroupChange, isAdminWorkspace, adminSelectorSubGroupId } =
    useSignalsSubGroupSelector({
      subGroupId: subGroup?.id,
      subGroups: activeGroups,
      setSubGroupId: handleSubGroupChange,
    });
  const botData = botGeneralData as BotData | undefined;
  const originalBotSubGroupId = botData?.sub_group_id;
  const defaultSubGroupId =
    originalBotSubGroupId ?? adminSelectorSubGroupId ?? querySubGroupId;

  useEffect(() => {
    if (activeGroups && initialized && !subGroup) {
      if (isEdit || defaultSubGroupId || isBacktesting) {
        const userSubGroup = defaultSubGroupId
          ? userGroupIdToData?.[defaultSubGroupId]
          : undefined;
        if (userSubGroup) {
          setSubGroup(userSubGroup);
        } else if (originalBotSubGroupId) {
          const typedBotData = botData as BotData;
          const featureFlags =
            typedBotData?.entry_grace_target_exchange ||
            typedBotData?.take_profit_grace_target_exchange
              ? [GRACE_TARGET_EXCHANGE_FEATURE_FLAG]
              : undefined;
          setSubGroup(
            addSubGroupExtraData({
              id: originalBotSubGroupId,
              image: typedBotData?.sub_group_image as string,
              name: typedBotData?.sub_group_name as string,
              feature_flags: featureFlags,
            })
          );
        } else {
          openMessage(getBotsInvalidGroupMessage("edited"), "warning");
          onInvalidGroup?.();
        }
      } else {
        let defaultPersonal = isAssetManager ? personalGroups[0] : undefined;
        if (!isTradingViewSignalsBot) {
          defaultPersonal = defaultPersonal ?? activeGroups[0];
        }
        if (defaultPersonal) {
          setSubGroup(defaultPersonal);
        }
      }
      addInitializedData(FormDataType.SUB_GROUP);
    }
  }, [activeGroups, subGroup, initialized]);

  const getEmptyImage = () => {
    const Empty = emptyStates[EmptyStatesType.Users];
    return (
      <Empty
        style={{
          height: 60,
          width: "100%",
          margin: "20px 0 4px 0",
        }}
      />
    );
  };

  const getYourGroupsTitle = () => {
    if (user?.is_whop_user && !selectorGroups.userGroups.length) {
      return (
        <EmptyWrapper>
          {getEmptyImage()}
          <Text>{t("empty_states.no_signals_groups")}</Text>
          <Text
            type="secondary"
            style={{
              paddingBottom: 16,
              maxWidth: 300,
              textAlign: "center",
              marginTop: 4,
            }}
          >
            {t("empty_states.whop_creator_no_signals_groups")}
          </Text>
        </EmptyWrapper>
      );
    }
    if (isNewLook && !selectorGroups.userGroups.length) {
      // Keeps the section from ending on a bare title.
      return (
        <div>
          {t("labels.your_groups")}
          <NewLookSectionEmpty>
            {t("empty_states.no_your_groups")}
          </NewLookSectionEmpty>
        </div>
      );
    }
    return t("labels.your_groups");
  };

  const getOptions = (): SelectOption[] => {
    let options: SelectOption[] =
      botType === BotType.TRADING_VIEW_SIGNALS && !isAdminWorkspace
        ? []
        : [{ title: getYourGroupsTitle(), options: selectorGroups.userGroups }];
    const hasGroups = !!selectorGroups.personalGroups.length;
    if (isAssetManager) {
      options = [
        {
          title: (
            <div>
              <Flex>
                <Text type="secondary" $size={typography.fontSizes.S}>
                  Personal Groups
                </Text>
                {hasGroups && (
                  <CreatePersonalGroupButton
                    isLink={true}
                    style={{ fontSize: typography.fontSizes.S }}
                    setModalOpen={setOpenPersonalGroupModal}
                  />
                )}
              </Flex>
              {!hasGroups && (
                <EmptyWrapper>
                  {getEmptyImage()}
                  <CreatePersonalGroupButton
                    isLink={true}
                    style={{ marginTop: 8 }}
                    setModalOpen={setOpenPersonalGroupModal}
                  />
                </EmptyWrapper>
              )}
            </div>
          ),
          options: selectorGroups.personalGroups,
        },
        ...options,
      ];
    }
    if (
      selectorGroups.freeTrialGroups.length &&
      !isSubUser &&
      !user?.isWhopExperience()
    ) {
      options.push({
        title: t("labels.free_trial_groups"),
        options: selectorGroups.freeTrialGroups,
      });
    }

    return options;
  };

  const getPlaceholder = () => {
    if (isSymbol(subGroup)) return t("labels.multi");
    return isBotEdit &&
      botGeneralData &&
      "channel_configuration_name" in botGeneralData
      ? botGeneralData.channel_configuration_name
      : undefined;
  };

  const subGroupSelectorData = {
    shouldShow:
      (isSignalsBot && !isChannelConfiguration) ||
      botType === BotType.TRADING_VIEW_SIGNALS,
    value: isSymbol(subGroup) ? undefined : subGroup?.id,
    onChange: onSubGroupChange,
    options: getOptions(),
    title: t("labels.signals_group"),
    disabled: isBotEdit,
    placeholder: getPlaceholder(),
  };

  return {
    subGroupSelectorData,
    subGroupError,
    isAdminWorkspace,
  };
};

export default useSubGroupSelector;

type SelectOption = {
  title: string | React.JSX.Element;
  options: BotSubGroup[];
};

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const NewLookSectionEmpty = styled.div`
  padding: 6px 0 4px;
  color: ${(p) => p.theme.colors.lightestText};
  font-size: 13px;
  line-height: 18px;
`;
